/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api/sipProfileApi.ts
 🗓️ Created: 2025-01-07
 🧩 Description:
    API functions for SIP Profiles.
    Centralizes all API calls related to SIP profiles.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import {
  SipProfile,
  ListSipProfilesResponse,
  SipProfilePayload,
  QuickListResponse,
  DomainListResponse,
  CategoryListResponse,
} from '../types';

/**
 * Fetch SIP Profiles with pagination and search
 */
export async function fetchSipProfiles(params: {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  search?: string;
}): Promise<ListSipProfilesResponse> {
  const queryParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.limit.toString(),
    sortBy: params.sortBy,
    sortOrder: params.sortOrder,
    ...(params.search && { search: params.search }),
  });

  const url = `${buildApiUrl('/api/sip-profiles')}?${queryParams}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: getApiHeaders(),
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const result = await response.json();

  if (!result.success) {
    throw new Error(result.message || 'Failed to fetch SIP Profiles');
  }

  return result;
}

/**
 * Fetch single SIP Profile by ID or UUID
 */
export async function fetchSipProfile(id: string | number): Promise<SipProfile> {
  const response = await fetch(buildApiUrl(`/api/sip-profiles/${id}`), {
    method: 'GET',
    headers: getApiHeaders(),
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const result = await response.json();

  if (!result.success) {
    throw new Error(result.message || 'Failed to fetch SIP Profile');
  }

  return result.data;
}

/**
 * Create new SIP Profile
 */
export async function createSipProfile(payload: SipProfilePayload): Promise<SipProfile> {
  const response = await fetch(buildApiUrl('/api/sip-profiles'), {
    method: 'POST',
    headers: getApiHeaders(),
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.message || 'Failed to create SIP Profile');
  }

  const result = await response.json();

  if (!result.success) {
    throw new Error(result.message || 'Failed to create SIP Profile');
  }

  return result.data;
}

/**
 * Update SIP Profile
 */
export async function updateSipProfile(
  id: string | number,
  payload: SipProfilePayload,
): Promise<SipProfile> {
  const response = await fetch(buildApiUrl(`/api/sip-profiles/${id}`), {
    method: 'PUT',
    headers: getApiHeaders(),
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.message || 'Failed to update SIP Profile');
  }

  const result = await response.json();

  if (!result.success) {
    throw new Error(result.message || 'Failed to update SIP Profile');
  }

  return result.data;
}

/**
 * Fetch domains list
 */
export async function fetchDomainsList(): Promise<DomainListResponse> {
  const response = await fetch(buildApiUrl('/api/sip-profiles/domains/list'), {
    method: 'GET',
    headers: getApiHeaders(),
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const result = await response.json();

  if (!result.success) {
    throw new Error(result.message || 'Failed to fetch domains');
  }

  return result;
}

/**
 * Fetch categories list
 */
export async function fetchCategoriesList(): Promise<CategoryListResponse> {
  const response = await fetch(buildApiUrl('/api/sip-profiles/categories/list'), {
    method: 'GET',
    headers: getApiHeaders(),
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const result = await response.json();

  if (!result.success) {
    throw new Error(result.message || 'Failed to fetch categories');
  }

  return result;
}

/**
 * Fetch quick list
 */
export async function fetchQuickList(search?: string): Promise<QuickListResponse> {
  const queryParams = new URLSearchParams();
  if (search) {
    queryParams.set('search', search);
  }

  const url = `${buildApiUrl('/api/sip-profiles/quicklist')}${queryParams.toString() ? `?${queryParams}` : ''}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: getApiHeaders(),
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const result = await response.json();

  if (!result.success) {
    throw new Error(result.message || 'Failed to fetch quick list');
  }

  return result;
}

